import * as signalR from '@microsoft/signalr';
import { apiConfig } from '../../config/api';

// Expose SignalR globally for Unity WebGL jslib interop
if (typeof window !== 'undefined') {
  (window as unknown as { signalR: typeof signalR }).signalR = signalR;
}

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error';

export interface SignalREventHandlers {
  onConnectionStateChange?: (state: ConnectionState) => void;
  onError?: (error: Error) => void;
  onReconnecting?: (error?: Error) => void;
  onReconnected?: (connectionId?: string) => void;
  onDisconnected?: (error?: Error) => void;
}

// Internal storage for multiple listeners
interface SignalREventListeners {
  connectionStateChange: Set<(state: ConnectionState) => void>;
  error: Set<(error: Error) => void>;
  reconnecting: Set<(error?: Error) => void>;
  reconnected: Set<(connectionId?: string) => void>;
  disconnected: Set<(error?: Error) => void>;
}

/**
 * SignalR service for managing hub connections
 */
export class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private eventListeners: SignalREventListeners = {
    connectionStateChange: new Set(),
    error: new Set(),
    reconnecting: new Set(),
    reconnected: new Set(),
    disconnected: new Set(),
  };
  private messageHandlers: Map<string, Set<(...args: unknown[]) => void>> = new Map();
  private userId: string | null = null;
  private storedAccessToken: string | null = null;

  constructor() {
    this.connection = null;
  }

  /**
   * Get current connection state
   */
  get state(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Get connection ID
   */
  get connectionId(): string | null {
    return this.connection?.connectionId ?? null;
  }

  /**
   * Check if connected
   */
  get isConnected(): boolean {
    return this.connectionState === 'connected';
  }

  /**
   * Set event handlers (adds to existing handlers, supports multiple listeners)
   */
  setEventHandlers(handlers: SignalREventHandlers): void {
    if (handlers.onConnectionStateChange) {
      this.eventListeners.connectionStateChange.add(handlers.onConnectionStateChange);
    }
    if (handlers.onError) {
      this.eventListeners.error.add(handlers.onError);
    }
    if (handlers.onReconnecting) {
      this.eventListeners.reconnecting.add(handlers.onReconnecting);
    }
    if (handlers.onReconnected) {
      this.eventListeners.reconnected.add(handlers.onReconnected);
    }
    if (handlers.onDisconnected) {
      this.eventListeners.disconnected.add(handlers.onDisconnected);
    }
  }

  /**
   * Connect to SignalR hub
   */
  async connect(userId?: string, accessToken?: string): Promise<void> {
    if (this.connectionState === 'connected' || this.connectionState === 'connecting') {
      console.log('[SignalR] Already connected or connecting');
      return;
    }

    this.userId = userId ?? null;
    if (accessToken) {
      this.storedAccessToken = accessToken;
    }

    this.setConnectionState('connecting');

    try {
      const hubUrl = apiConfig.signalR.hubUrl;
      console.log('[SignalR] Connecting to:', hubUrl);

      // Use stored access token for reconnections
      const tokenToUse = accessToken ?? this.storedAccessToken;

      const connectionBuilder = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: tokenToUse ? () => tokenToUse : undefined,
          // Use negotiate flow for proper WebSocket connection through proxy
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            // Exponential backoff: 0, 2, 4, 8, 16, 30 seconds
            const delays = [0, 2000, 4000, 8000, 16000, 30000];
            return delays[Math.min(retryContext.previousRetryCount, delays.length - 1)];
          },
        })
        .configureLogging(signalR.LogLevel.Information);

      this.connection = connectionBuilder.build();

      // Handle server-sent 'Connected' event (sent by OnConnectedAsync)
      this.connection.on('Connected', (connectionId: string) => {
        console.log('[SignalR] Server confirmed connection:', connectionId);
      });

      // The room hub broadcasts high-frequency player-sync frames (position/anim) to the
      // whole room group. Those are consumed by the Unity WebGL bridge's OWN roomHub
      // connection (flexoBridgeSignalR), not this app/presence connection. Register a
      // no-op so @microsoft/signalr doesn't log "No client method 'receiveplayersync'
      // found" ~20x/sec while in a room.
      this.connection.on('ReceivePlayerSync', () => { /* handled by the Unity bridge connection */ });

      // Set up connection event handlers
      this.connection.onreconnecting((error) => {
        this.setConnectionState('reconnecting');
        this.eventListeners.reconnecting.forEach(handler => handler(error));
      });

      this.connection.onreconnected((connectionId) => {
        this.setConnectionState('connected');
        this.eventListeners.reconnected.forEach(handler => handler(connectionId));
        // Re-register user after reconnect (heals room + host-console group membership).
        if (this.userId && this.storedAccessToken) {
          this.registerUser(this.userId);
        }
      });

      this.connection.onclose((error) => {
        this.setConnectionState('disconnected');
        this.eventListeners.disconnected.forEach(handler => handler(error));
        if (error) {
          this.eventListeners.error.forEach(handler => handler(error));
        }
      });

      // Re-register all message handlers
      this.messageHandlers.forEach((handlers, methodName) => {
        handlers.forEach((handler) => {
          this.connection?.on(methodName, handler);
        });
      });

      await this.connection.start();
      this.setConnectionState('connected');
      console.log('[SignalR] Connected with ID:', this.connection.connectionId);

      // Session restore / group healing. ReconnectUser requires an authenticated connection
      // (V3 A0-6), so skip it on an anonymous connection — there is nothing to restore there and
      // the invocation would only produce a noisy UNAUTHENTICATED rejection.
      if (userId && tokenToUse) {
        await this.registerUser(userId);
      }
    } catch (error) {
      console.error('[SignalR] Connection failed:', error);
      this.setConnectionState('error');
      this.eventListeners.error.forEach(handler => handler(error as Error));
      throw error;
    }
  }

  /**
   * V3 A0-6 — rebind the connection to a (new) access token.
   *
   * SignalR resolves the principal ONCE, during the negotiate/handshake, and reuses it for the
   * life of the connection. A connection opened before sign-in therefore stays anonymous forever,
   * no matter how many times the app signs in over it — and every hub method except the two
   * bootstrap ones now refuses an anonymous caller. So after login/registration the connection
   * must actually be torn down and reopened with the token attached.
   */
  async reconnectWithToken(userId: string, accessToken: string): Promise<void> {
    try {
      await this.disconnect();
    } catch (e) {
      console.warn('[SignalR] Failed to close the anonymous connection cleanly:', e);
      this.connection = null;
      this.setConnectionState('disconnected');
    }
    this.storedAccessToken = accessToken;
    await this.connect(userId, accessToken);
  }

  /**
   * Disconnect from hub
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.setConnectionState('disconnected');
    }
  }

  /**
   * Register a message handler
   */
  on<T extends unknown[]>(methodName: string, handler: (...args: T) => void): () => void {
    if (!this.messageHandlers.has(methodName)) {
      this.messageHandlers.set(methodName, new Set());
    }

    const handlers = this.messageHandlers.get(methodName)!;
    const wrappedHandler = handler as (...args: unknown[]) => void;
    handlers.add(wrappedHandler);

    // Register with connection if already connected
    if (this.connection) {
      this.connection.on(methodName, wrappedHandler);
    }

    // Return unsubscribe function
    return () => {
      handlers.delete(wrappedHandler);
      this.connection?.off(methodName, wrappedHandler);
    };
  }

  /**
   * Remove a message handler
   */
  off(methodName: string, handler?: (...args: unknown[]) => void): void {
    if (handler) {
      const handlers = this.messageHandlers.get(methodName);
      if (handlers) {
        handlers.delete(handler);
      }
      this.connection?.off(methodName, handler);
    } else {
      this.messageHandlers.delete(methodName);
      // Note: SignalR doesn't have a way to remove all handlers for a method
    }
  }

  /**
   * Invoke a hub method
   */
  async invoke<T = void>(methodName: string, ...args: unknown[]): Promise<T> {
    if (!this.connection || this.connectionState !== 'connected') {
      throw new Error('Not connected to SignalR hub');
    }
    return await this.connection.invoke<T>(methodName, ...args);
  }

  /**
   * Send a message (fire and forget)
   */
  async send(methodName: string, ...args: unknown[]): Promise<void> {
    if (!this.connection || this.connectionState !== 'connected') {
      throw new Error('Not connected to SignalR hub');
    }
    await this.connection.send(methodName, ...args);
  }

  /**
   * Reconnect existing user with the hub (session restore)
   */
  private async registerUser(userId: string): Promise<void> {
    try {
      await this.invoke('ReconnectUser', userId);
      console.log('[SignalR] User reconnected:', userId);
    } catch (error) {
      console.error('[SignalR] Failed to reconnect user:', error);
    }
  }

  private setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.eventListeners.connectionStateChange.forEach(handler => handler(state));
  }
}

// Singleton instance
export const signalRService = new SignalRService();
